import axios from "axios";
import ApiClient from "../ApiClient";
import NewUUID from "../../utils/randy/NewUUID";

export const listUsersTemplates = async (csrfToken, groupId = null) => {
    // groupId can be:
    // - null/undefined/'': fetch bills without a group_id (default group)
    // - 'all': fetch bills from all groups (bill bar shows all)
    // - valid UUID: fetch bills for that specific group
    let url = '/api/bills/templates';
    if (groupId === 'all') {
        url += '?group_id=all';
    } else if (groupId === null || groupId === undefined || groupId === '' || (Array.isArray(groupId) && groupId.length === 0)) {
        // Default group - no query param
    } else {
        const isValidUUID = typeof groupId === 'string' && groupId.length === 36 && groupId.includes('-');
        if (isValidUUID) {
            url += `?group_id=${groupId}`;
        }
    }
    const response = await ApiClient.get(url, {
        headers: {
            accept: "text/json",
            request_id: NewUUID(),
            "X-CSRF-Token": csrfToken
        },
    });
    return response.data.result
}

// export const getUsersTemplate = (authToken, csrfToken) => {
//     return async ({queryKey}) => {
//         const [_, id] = queryKey
//
//         const response = await ApiClient.get(`/api/bills/templates/${id.toString()}`, {
//             headers: {
//                 accept: "text/json",
//                 request_id: NewUUID(),
//                 "X-CSRF-Token": csrfToken
//             },
//         });
//         return response.data.result
//     }
// }
export const getUsersTemplate = async (id, csrfToken) => {
    const response = await ApiClient.get(`/api/bills/templates/${id}`, {
        headers: {
            accept: "application/json",  // It's typically "application/json", not "text/json"
            request_id: NewUUID(),
            "X-CSRF-Token": csrfToken
        },
    });
    return response.data.result;
}

export const putUsersTemplate = (csrfToken) => {
    return async (formData) => {
        if (!formData.id) {
            throw new Error('Template ID is required');
        }
        const response = await ApiClient.put(`/api/bills/templates/${formData.id}`, {
            name: formData.name,
            description: formData.description,
            typical_cost: parseInt(formData.typical_cost),
            archived: formData.archived !== undefined ? formData.archived : false,
        }, {
            headers: {
                accept: "text/json",
                request_id: NewUUID(),
                "X-CSRF-Token": csrfToken
            },
        });
        return response.data.result
    }
}

export const moveBillToGroup = async (billId, groupId, csrfToken) => {
    // groupId can be null to remove bill from group (move to default group)
    // Backend expects null or a valid UUID string
    const response = await ApiClient.put(`/api/bills/templates/${billId}/group`, {
        group_id: groupId === null || groupId === undefined ? null : groupId, // null removes from group, valid UUID moves to that group
    }, {
        headers: {
            accept: "application/json",
            request_id: NewUUID(),
            "X-CSRF-Token": csrfToken
        },
    });
    return response.data.result
}

export const updateBillFavorite = async (id, favorite, csrfToken) => {
    if (!id) {
        throw new Error('Template ID is required');
    }
    const response = await ApiClient.put(`/api/bills/templates/${String(id)}/favorite`, {
        favorite: favorite,
    }, {
        headers: {
            accept: "text/json",
            request_id: NewUUID(),
            "X-CSRF-Token": csrfToken
        },
    });
    return response.data.result;
}

/** Default bill: aggregated statements and transactions from all shadow bills the user is in.
 * Backend uses default limits (statements: 10, transactions: 20) when limit/offset are omitted.
 * Returns result: { statements, transactions, owned_statement_ids, total_statements, total_transactions, has_more_statements, has_more_transactions }.
 */
export const getDefaultBillDetails = async (csrfToken) => {
    const response = await ApiClient.get('/api/bills/default', {
        headers: {
            accept: 'application/json',
            request_id: NewUUID(),
            'X-CSRF-Token': csrfToken
        },
    });
    return response.data.result;
};

/** Fetch next page of default-bill statements (cursor-based). Returns { result, next_cursor }. Limit from backend. */
export const fetchDefaultBillStatementsPage = async (csrfToken, cursor = '') => {
    let url = '/api/bills/default/statements';
    if (cursor) url += `?cursor=${encodeURIComponent(cursor)}`;
    const response = await ApiClient.get(url, {
        headers: {
            accept: 'application/json',
            request_id: NewUUID(),
            'X-CSRF-Token': csrfToken
        },
    });
    return {
        result: response.data.result ?? [],
        next_cursor: response.data.next_cursor ?? ''
    };
};

/** Fetch next page of default-bill transactions (cursor-based). Returns { result, next_cursor }. Limit from backend. */
export const fetchDefaultBillTransactionsPage = async (csrfToken, cursor = '') => {
    let url = '/api/bills/default/transactions';
    if (cursor) url += `?cursor=${encodeURIComponent(cursor)}`;
    const response = await ApiClient.get(url, {
        headers: {
            accept: 'application/json',
            request_id: NewUUID(),
            'X-CSRF-Token': csrfToken
        },
    });
    return {
        result: response.data.result ?? [],
        next_cursor: response.data.next_cursor ?? ''
    };
};

/** Dashboard: aggregated statements and transactions for all bills in the selected group (or all groups).
 * groupId: null | undefined | 'all' = all groups; UUID string = that group. */
export const getDashboardDetails = async (csrfToken, groupId = null) => {
    let url = '/api/dashboard';
    if (groupId != null && groupId !== '' && groupId !== 'all') {
        url += `?group_id=${encodeURIComponent(groupId)}`;
    } else {
        url += '?group_id=all';
    }
    const response = await ApiClient.get(url, {
        headers: {
            accept: 'application/json',
            request_id: NewUUID(),
            'X-CSRF-Token': csrfToken
        },
    });
    return response.data.result;
};

/** Move a default-bill statement (and its transactions) to another bill. Only for statements you own. */
export const moveDefaultStatementToBill = async (csrfToken, statementId, targetBillId) => {
    const target = typeof targetBillId === 'string' ? targetBillId.trim() : String(targetBillId || '').trim();
    if (!target) {
        throw new Error('target_bill_id is required');
    }
    const response = await ApiClient.post(
        `/api/bills/default/statements/${encodeURIComponent(statementId)}/move`,
        { target_bill_id: target },
        {
            headers: {
                accept: 'application/json',
                'Content-Type': 'application/json',
                request_id: NewUUID(),
                'X-CSRF-Token': csrfToken
            },
        }
    );
    return response.data.result;
};

/** Create a default-bill statement (quick split): even split among participant_user_ids. */
export const postDefaultStatement = async (csrfToken, { participant_user_ids, cost }) => {
    const response = await ApiClient.post('/api/bills/default/statements', {
        participant_user_ids,
        cost: typeof cost === 'number' ? cost : parseFloat(cost),
    }, {
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            request_id: NewUUID(),
            'X-CSRF-Token': csrfToken
        },
    });
    return response.data.result;
};

